// ── Welle B2: Zeugnis-Text — gespeichert je Fach und Halbjahr, bearbeitbar ──────
// Öffnen zeigt den gespeicherten Text. „Mit KI schreiben“ erzeugt und speichert; gibt es schon
// einen Text, fragt das Fenster vorher („Gespeicherten Text ersetzen?“) — die Rückfrage steht
// im Fenster selbst (ein zweites Overlay über dem Sheet wäre blockiert).
import React, { useEffect, useState } from 'react';
import { MIcon } from '../../../lib/micon';
import { apiGet, apiPut } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { LdSheet } from '../../../components/ui/LdSheet';
import { LdFeld, LdTextfeld, LdChips, LdKiHinweis } from '../../../components/ld/LdFenster';
import '../../../components/diktat/diktat-review.css';
import { datumTTMMJJJJ } from '../../../lib/datum';
import './ZeugnisTextSheet.css';

type Text = { fach: string; text: string; quelle: 'ki' | 'bearbeitet'; erstellt_am: string; geaendert_am: string };
type Antwort = { halbjahr: 'HJ1' | 'HJ2'; schuljahr: string; faecher: string[]; texte: Text[] };

export function ZeugnisTextSheet({ open, schuelerId, schuelerName, klasse, isMobile, onClose, onGeaendert }: {
  open: boolean; schuelerId: number; schuelerName: string; klasse?: string | null; isMobile: boolean; onClose: () => void; onGeaendert: () => void;
}) {
  const [daten, setDaten] = useState<Antwort | null>(null);
  const [fach, setFach] = useState('');
  const [text, setText] = useState('');
  const [fehler, setFehler] = useState('');
  const [erzeugt, setErzeugt] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [rueckfrage, setRueckfrage] = useState(false);

  const gespeichert = daten?.texte.find(t => t.fach === fach) ?? null;
  const geaendert = !!gespeichert && text.trim() !== gespeichert.text;

  useEffect(() => {
    if (!open) return;
    setDaten(null); setFehler(''); setRueckfrage(false);
    apiGet<Antwort>(`/api/schueler/${schuelerId}/zeugnis-texte`).then(d => {
      setDaten(d);
      const start = d.texte[0]?.fach ?? d.faecher[0] ?? '';
      setFach(start); setText(d.texte.find(t => t.fach === start)?.text ?? '');
    }).catch((err: any) => setFehler(err?.message ?? 'Konnte nicht geladen werden.'));
  }, [open, schuelerId]);

  function fachWaehlen(f: string) {
    setFach(f); setRueckfrage(false); setFehler('');
    setText(daten?.texte.find(t => t.fach === f)?.text ?? '');
  }

  async function erzeugen() {
    if (!daten || !fach) return;
    setRueckfrage(false); setErzeugt(true); setFehler('');
    try {
      const q = new URLSearchParams({ fach, halbjahr: daten.halbjahr, schuljahr: daten.schuljahr });
      const r = await apiGet<{ entwurf: string; gespeichert?: Text }>(`/api/schueler/${schuelerId}/zeugnis-entwurf?${q}`);
      const neu = r.gespeichert ?? { fach, text: r.entwurf, quelle: 'ki' as const, erstellt_am: '', geaendert_am: '' };
      setDaten(d => d ? { ...d, texte: [...d.texte.filter(t => t.fach !== fach), neu] } : d);
      setText(neu.text);
      toast('Zeugnis-Text gespeichert', 'success');
      onGeaendert();
    } catch (err: any) { setFehler(err?.message ?? 'Der Text konnte nicht erzeugt werden.'); }
    finally { setErzeugt(false); }
  }

  async function speichern() {
    if (!daten || !fach || !text.trim()) return;
    setSpeichert(true); setFehler('');
    try {
      const t = await apiPut<Text>(`/api/schueler/${schuelerId}/zeugnis-text`, { fach, halbjahr: daten.halbjahr, schuljahr: daten.schuljahr, text });
      setDaten(d => d ? { ...d, texte: [...d.texte.filter(x => x.fach !== fach), t] } : d);
      setText(t.text);
      toast('Zeugnis-Text gespeichert', 'success');
      onGeaendert();
    } catch (err: any) { setFehler(err?.message ?? 'Speichern fehlgeschlagen.'); }
    finally { setSpeichert(false); }
  }

  // „2026/2027“ → „2026/27“
  const hj = daten ? `${daten.halbjahr === 'HJ1' ? '1.' : '2.'} Halbjahr ${daten.schuljahr.replace(/^(\d{4})\/\d{2}(\d{2})$/, '$1/$2')}` : '';
  // „Allgemein“ zuerst, dann die eigenen Fächer
  const faecher = daten ? [...daten.faecher.filter(f => f === 'Allgemein'), ...daten.faecher.filter(f => f !== 'Allgemein')] : [];
  const kannKopieren = !!text.trim() && !!gespeichert && !geaendert;
  const fuss = daten && daten.faecher.length > 0 ? (
    <>
      <span className="ld-fe-fuss__links ld-fe-fuss__links--zeile">
        <button type="button" className="ld-kl-link" disabled={erzeugt} onClick={() => (gespeichert ? setRueckfrage(true) : erzeugen())}>
          <MIcon name="auto_awesome" size={17} />{erzeugt ? 'Wird geschrieben …' : gespeichert ? 'Neu formulieren' : 'Entwurf schreiben'}</button>
      </span>
      <button type="button" className="ld-fe-knopf ld-fe-knopf--zweit" disabled={!kannKopieren}
        onClick={() => navigator.clipboard?.writeText(text).then(() => toast('Text kopiert', 'success'), () => toast('Kopieren fehlgeschlagen', 'error'))}>
        <MIcon name="content_copy" size={17} />Kopieren</button>
      <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt" onClick={speichern}
        disabled={speichert || erzeugt || !text.trim() || (!!gespeichert && !geaendert)}>{speichert ? 'Bitte warten …' : 'Speichern'}</button>
    </>
  ) : undefined;
  return (
    <LdSheet open={open} onClose={onClose} maxWidth={620}
      kopf={{ titel: 'Zeugnis-Text', kontext: [schuelerName, klasse, hj].filter(Boolean).join(', ') }} fuss={fuss}>
      <div className="ld-zt">
        {!daten && !fehler && <div className="ld-pu-laedt"><span /><span /></div>}
        {daten && daten.faecher.length === 0 && <p className="ld-fe-text">Du unterrichtest in dieser Klasse kein Fach, hier gibt es keinen Zeugnis-Text von dir.</p>}
        {daten && daten.faecher.length > 0 && (<>
          <LdFeld label="Text für">
            <LdChips aria-label="Text für" wert={fach} onChange={fachWaehlen}
              optionen={faecher.map(f => ({ wert: f, label: f, icon: daten.texte.some(t => t.fach === f) ? 'check' : undefined }))} />
          </LdFeld>
          {gespeichert?.quelle === 'ki' && !geaendert && (
            <LdKiHinweis>Entwurf aus deinen Noten und Notizen{fach !== 'Allgemein' ? ` in ${fach}` : ''}. Bitte prüfen und anpassen.</LdKiHinweis>
          )}
          <LdFeld label="Text" rechts={`${text.length} / ${ZEICHEN_MAX}`}
            hinweis={gespeichert
              ? `Gespeichert am ${gespeichertAm(gespeichert.geaendert_am)}${gespeichert.quelle === 'bearbeitet' ? ', von dir bearbeitet' : ''}`
              : 'Für dieses Fach gibt es in diesem Halbjahr noch keinen Text.'}>
            <LdTextfeld rows={isMobile ? 11 : 10} value={text} onChange={e => setText(e.target.value.slice(0, ZEICHEN_MAX))}
              disabled={erzeugt} aria-label={`Zeugnis-Text ${fach}`}
              placeholder={erzeugt ? 'Der Entwurf wird geschrieben …' : 'Selbst schreiben oder einen Entwurf schreiben lassen.'} />
          </LdFeld>
          {fehler && <p className="ld-fe-fehler" role="alert">{fehler}</p>}
          {rueckfrage && (
            <div className="ld-zt__rueckfrage" role="alertdialog" aria-label="Gespeicherten Text ersetzen?">
              <b>Gespeicherten Text ersetzen?</b>
              <span>Es wird ein neuer Entwurf für {fach} geschrieben. Der gespeicherte Text geht dabei verloren.</span>
              <div>
                <button type="button" className="ld-dr-knopf" onClick={() => setRueckfrage(false)}>Behalten</button>
                <button type="button" className="ld-dr-knopf ld-zt__ersetzen" onClick={erzeugen}>Ersetzen</button>
              </div>
            </div>
          )}
        </>)}
        {fehler && !daten && <p className="ld-fe-fehler" role="alert">{fehler}</p>}
      </div>
    </LdSheet>
  );
}

const ZEICHEN_MAX = 4000;
const z2 = (n: number) => String(n).padStart(2, '0');
/** SQLite-Zeit (UTC, „JJJJ-MM-TT HH:MM:SS“) → „22.10., 18:40“ */
function gespeichertAm(ts: string): string {
  const d = new Date(/Z|[+-]\d\d:?\d\d$/.test(ts) ? ts : `${ts.replace(' ', 'T')}Z`);
  return isNaN(d.getTime()) ? datumTTMMJJJJ(ts) : `${z2(d.getDate())}.${z2(d.getMonth() + 1)}., ${z2(d.getHours())}:${z2(d.getMinutes())}`;
}
